import { Button, Stack } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import {
  Control,
  FieldArrayPath,
  FieldErrors,
  useFieldArray,
  useWatch,
} from 'react-hook-form'
import { InformationModal } from 'utoniq-ui'

import {
  ConsoleCampaignItem,
  ConsoleCampaignItemBox,
  FileType,
} from '@/components/organisms'
import {
  Form,
  handleError,
  useForm,
  UseFormProps,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { captureError } from '@/core/errors'
import { PickFile } from '@/core/native/file'

export type CampaignItemFormProps = {
  items: ConsoleCampaignItem[]
  uploading?: boolean
  onSubmit: (values: CampaignItemValues) => Promise<void>
  onUploadFile: (
    fileType: 'image' | 'benefit',
    index: number
  ) => Promise<
    | { fileUrl: string; tmpPath: string }
    | { originalFile: PickFile; uri: string }
  >
}

type CampaignItemValues = {
  items: ConsoleCampaignItem[]
}

const schema: UseFormProps['schema'] = o => ({
  items: o.array().items(
    o.object({
      image: o.object({
        front: {
          fileUrl: o.string().required(),
          tmpPath: o.string().required(),
        },
        back: {
          uri: o.string().required(),
        },
      }),
      hasBenefit: o.boolean().allow().optional(),
      benefit: o
        .object({
          uri: o.string().optional().allow(''),
          originalFile: o.any().allow(null).optional(),
          type: o.string(),
        })
        .allow(null)
        .optional(),
      rarity: o.object({
        id: o.string(),
        name: o.string(),
      }),
    })
  ),
})

export const CampaignItemForm: Organism<CampaignItemFormProps> = ({
  items,
  onSubmit,
  onUploadFile,
  uploading,
}) => {
  const { t } = useTranslation('consoleCampaignDetail')
  const { router, routerPages } = useRouter()
  const { showMessage, showError } = useMessage()
  const [open, setOpen] = useState<boolean>(false)

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    trigger,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CampaignItemValues>({
    schema,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { items: [] },
  })

  useEffect(() => {
    reset({ items }, { keepErrors: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  const onSubmitForm = useCallback(() => {
    setOpen(true)
  }, [])

  const onSubmitEntryCampaign = async (inputs: CampaignItemValues) => {
    await onSubmit(inputs)
      .then(() => {
        showMessage(t('success'))
        router.push(routerPages.ConsoleCampaigns)
      })
      .catch(e => {
        captureError(e, 'CampaignItemForm')
        handleError<CampaignItemValues>(e, setError, showError)
      })

    return
  }

  return (
    <Form
      id="campaign-item-form"
      sx={{ px: 5, py: 3, mt: 3 }}
      onSubmit={handleSubmit(onSubmitForm)}
    >
      <ItemArrayCell
        name="items"
        items={items}
        control={control}
        errors={errors}
        uploading={!!uploading}
        onUploadFile={async (fileType, index) => {
          await onUploadFile(fileType, index)
            .then(value => {
              if ('originalFile' in value) {
                setValue(
                  `items.${index}.benefit.originalFile`,
                  value.originalFile
                )
                setValue(`items.${index}.benefit.uri`, value.uri)
                setValue(
                  `items.${index}.benefit.type`,
                  value.originalFile.mimeType.includes('video')
                    ? FileType.Video
                    : FileType.Audio
                )
                return trigger(`items.${index}.benefit`)
              }
              setValue(`items.${index}.image.front`, value)
              return trigger(`items.${index}.image.front`)
            })
            .catch(error => {
              if (error.message === 'uploadErrorMaximumSize') {
                showError(new Error(t('uploadErrorMaximumSize')))
                return
              }
              if (error.message === 'uploadErrorImageOnly') {
                showError(new Error(t('uploadErrorImageOnly')))
                return
              }
              if (error.message === 'uploadErrorAudioOrVideoOnly') {
                showError(new Error(t('uploadErrorAudioOrVideoOnly')))
                return
              }
              showError(error)
            })
        }}
        onRemoveFile={index => {
          setValue(`items.${index}.benefit`, {
            uri: '',
            originalFile: undefined,
            type: FileType.Image,
          })
          return trigger(`items.${index}.benefit`)
        }}
      />
      <InformationModal
        title={t('entryModalTitle')}
        content={t('entryModalDesc')}
        open={open}
        onClose={() => setOpen(false)}
        cancelText={t('cancel')}
        buttons={
          <Button
            color="primary"
            size="large"
            onClick={handleSubmit(onSubmitEntryCampaign)}
            sx={{ minWidth: 212 }}
            loading={isSubmitting}
          >
            {t('entryBtn')}
          </Button>
        }
      />
    </Form>
  )
}

type ItemArrayCellProps = {
  name: FieldArrayPath<CampaignItemValues>
  items: CampaignItemValues['items']
  control: Control<CampaignItemValues>
  errors: FieldErrors<CampaignItemValues>
  onUploadFile: (fileType: 'image' | 'benefit', index: number) => Promise<void>
  onRemoveFile: (index: number) => void
  uploading: boolean
}
const ItemArrayCell = ({
  name,
  control,
  errors,
  onUploadFile,
  onRemoveFile,
  uploading,
}: ItemArrayCellProps) => {
  const { fields } = useFieldArray({ control, name })
  const itemsWatch = useWatch({ control, name })
  return (
    <CellFrame>
      <Stack spacing={5}>
        {fields.map((field, index) => (
          <ConsoleCampaignItemBox
            key={field.id}
            item={itemsWatch[index]}
            uploading={uploading}
            onUploadFile={fileType => onUploadFile(fileType, index)}
            onRemoveFile={() => onRemoveFile(index)}
            errors={errors.items?.[index]}
          />
        ))}
      </Stack>
    </CellFrame>
  )
}
